import {useRef, useState, useEffect} from 'react'
import {Upload, FileText, Image, FileCode, File, Trash2, Eye, Download, Loader2} from 'lucide-react'
import type {Attachment} from '../../types'
import {useApp} from '../../context/AppContext'
import {formatFileSize, formatRelativeDate, isPreviewableFile} from '../../utils/helpers'
import {uploadFile} from '../../services/api'
import FilePreviewModal from '../common/FilePreviewModal'
import ConfirmDialog from '../common/ConfirmDialog'

interface TaskAttachmentsProps {
  taskId: string
  attachments: Attachment[]
  onBusyChange?: (busy: boolean) => void
}

function getFileIcon(type: string) {
  if (type.startsWith('image/')) return Image
  if (type.includes('pdf')) return FileText
  if (type.includes('code') || type.includes('javascript') || type.includes('typescript'))
    return FileCode
  return File
}

export default function TaskAttachments({taskId, attachments, onBusyChange}: TaskAttachmentsProps) {
  const {getUserById, apiAddAttachment, apiRemoveAttachment} = useApp()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [previewFile, setPreviewFile] = useState<Attachment | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Attachment | null>(null)
  const [uploading, setUploading] = useState(false)

  const [uploadError, setUploadError] = useState('')
  const [failedFiles, setFailedFiles] = useState<File[]>([])
  const [uploadProgress, setUploadProgress] = useState('')
  const uploadedFilesRef = useRef(new Map<File, number>())
  const uploadLockRef = useRef(false)
  useEffect(() => {
    onBusyChange?.(uploading)
  }, [uploading, onBusyChange])

  const uploadFiles = async (files: File[]) => {
    if (uploadLockRef.current || files.length === 0) return
    uploadLockRef.current = true
    setUploading(true)
    setUploadError('')
    setFailedFiles([])
    const failed: File[] = []
    try {
      for (const [index, file] of files.entries()) {
        setUploadProgress(`${index + 1} de ${files.length}: ${file.name}`)
        try {
          let uploadId = uploadedFilesRef.current.get(file)
          if (uploadId === undefined) {
            uploadId = (await uploadFile(file)).id_upload
            uploadedFilesRef.current.set(file, uploadId)
          }
          await apiAddAttachment(taskId, {
            name: file.name,
            size: file.size,
            type: file.type,
            id_upload: uploadId,
          })
          uploadedFilesRef.current.delete(file)
        } catch {
          failed.push(file)
        }
      }
      setFailedFiles(failed)
      if (failed.length > 0)
        setUploadError(
          `${failed.length} arquivo(s) não foram enviados. Os demais anexos foram salvos.`
        )
    } finally {
      uploadLockRef.current = false
      setUploading(false)
      setUploadProgress('')
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    void uploadFiles(Array.from(event.target.files || []))
  }

  return (
    <div
      aria-busy={uploading}
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault()
        void uploadFiles(Array.from(event.dataTransfer.files))
      }}
    >
      <div className='flex items-center justify-between gap-3 mb-3'>
        <h4 className='text-sm font-semibold text-slate-700'>Anexos ({attachments.length})</h4>
        <button
          type='button'
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className='devops-secondary-button text-xs disabled:opacity-50'
        >
          {uploading ? <Loader2 size={14} className='animate-spin' /> : <Upload size={14} />}
          {uploading ? 'Enviando...' : 'Adicionar arquivos'}
        </button>
        <input
          ref={fileInputRef}
          type='file'
          multiple
          onChange={handleFileChange}
          className='hidden'
          aria-label='Enviar arquivos'
          disabled={uploading}
        />
      </div>

      {uploading && (
        <p role='status' className='rounded-xl bg-brand-50 p-3 text-xs text-brand-700 break-words'>
          Enviando {uploadProgress}
        </p>
      )}
      {uploadError && (
        <div role='alert' className='mb-3 rounded-xl bg-red-50 p-3 text-sm text-red-700'>
          <p className='m-0'>{uploadError}</p>
          <p className='m-0 mt-1 text-xs break-words'>
            {failedFiles.map((file) => file.name).join(', ')}
          </p>
          <button
            type='button'
            disabled={uploading}
            onClick={() => void uploadFiles(failedFiles)}
            className='mt-2 font-semibold underline'
          >
            Tentar novamente os arquivos pendentes
          </button>
        </div>
      )}
      <div className='space-y-2'>
        {attachments.map((attachment) => {
          const Icon = getFileIcon(attachment.type)
          const uploader = getUserById(attachment.uploadedBy)

          return (
            <div
              key={attachment.id}
              className='flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100 hover:border-slate-200 transition-colors group'
            >
              <div className='w-9 h-9 rounded-lg bg-brand-50 flex items-center justify-center shrink-0'>
                <Icon size={18} className='text-brand-500' />
              </div>
              <div className='flex-1 min-w-0'>
                <p className='text-sm font-medium text-slate-700 truncate'>{attachment.name}</p>
                <p className='text-xs text-slate-400'>
                  {formatFileSize(attachment.size)} &middot;{' '}
                  {uploader?.name || 'Usuário indisponível'} &middot;{' '}
                  {formatRelativeDate(attachment.uploadedAt)}
                </p>
              </div>
              <div className='flex items-center gap-1 group-hover:opacity-100 transition-all'>
                {isPreviewableFile(attachment.type) && attachment.url && (
                  <button
                    onClick={() => setPreviewFile(attachment)}
                    className='p-1.5 rounded-lg text-slate-300 hover:text-brand-500 hover:bg-brand-50 transition-colors'
                    aria-label={`Visualizar ${attachment.name}`}
                  >
                    <Eye size={14} />
                  </button>
                )}
                {attachment.url && (
                  <a
                    href={attachment.url}
                    download={attachment.name}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='p-1.5 rounded-lg text-slate-300 hover:text-emerald-500 hover:bg-emerald-50 transition-colors'
                    aria-label={`Baixar ${attachment.name}`}
                  >
                    <Download size={14} />
                  </a>
                )}
                <button
                  onClick={() => setDeleteTarget(attachment)}
                  className='p-1.5 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors'
                  aria-label={`Remover ${attachment.name}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          )
        })}

        {attachments.length === 0 && (
          <div className='text-center py-6 rounded-xl border-2 border-dashed border-slate-200'>
            <Upload size={24} className='mx-auto text-slate-300 mb-2' />
            <p className='text-sm text-slate-400'>Nenhum anexo ainda</p>
            <p className='text-xs text-slate-300 mt-1'>
              Arraste arquivos aqui ou use Adicionar arquivos
            </p>
          </div>
        )}
      </div>

      {previewFile && previewFile.url && (
        <FilePreviewModal
          isOpen={!!previewFile}
          onClose={() => setPreviewFile(null)}
          fileName={previewFile.name}
          fileType={previewFile.type}
          fileUrl={previewFile.url}
        />
      )}

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title='Remover anexo'
        message={`Deseja remover o anexo “${
          deleteTarget?.name || ''
        }”? Esta ação não pode ser desfeita.`}
        confirmLabel='Remover'
        onConfirm={async () => {
          if (deleteTarget) {
            await apiRemoveAttachment(deleteTarget.id)
          }
          setDeleteTarget(null)
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  )
}
